// VIDEO-EDITING — localStorage helpers for unsaved draft buckets.
//
// The Zustand store owns the in-memory draft state and uses Zustand's
// `persist` middleware to push the whole `drafts` namespace under a
// single localStorage key. This module exists for callers (the hook,
// future tests, future tools) that need to read or wipe the persisted
// snapshot without going through the store. Keeping the key + version
// here also lets the persist `merge` and the rest of the editor agree on
// one source of truth.

export const DRAFT_STORAGE_KEY = "video-editor-drafts";
export const DRAFT_STORAGE_VERSION = 1;

// Cap on how many project drafts we persist; older entries get pruned by
// the store when a new project is hydrated. Keeps localStorage from
// growing unbounded if a user touches many videos.
export const DRAFT_MAX_ENTRIES = 10;

// Debounce window for write-through saves while the user is typing.
// Short enough that an accidental refresh keeps the last few keystrokes;
// long enough that we don't thrash localStorage.
export const DRAFT_SAVE_DEBOUNCE_MS = 400;

interface PersistShape {
  state?: {
    drafts?: Record<string, unknown>;
  };
  version?: number;
}

function readRaw(): PersistShape | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistShape;
  } catch {
    return null;
  }
}

function writeRaw(value: PersistShape): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Quota errors are non-fatal — the in-memory store keeps working.
  }
}

export function clearPersistedDraft(projectId: string): void {
  const raw = readRaw();
  if (!raw?.state?.drafts) return;
  if (!(projectId in raw.state.drafts)) return;
  const { [projectId]: _omitted, ...rest } = raw.state.drafts;
  void _omitted;
  writeRaw({
    ...raw,
    state: { ...raw.state, drafts: rest },
  });
}

export function readPersistedDraft(projectId: string): unknown {
  const raw = readRaw();
  return raw?.state?.drafts?.[projectId] ?? null;
}

export function clearAllPersistedDrafts(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Debounce helper used by hooks/useDraftPersistence.ts. Returns a
// `{ schedule, flush, cancel }` tuple so the hook can fire the trailing
// write on pagehide.
export interface DebouncedSaver {
  schedule: () => void;
  flush: () => void;
  cancel: () => void;
}

export function createDebouncedSaver(
  save: () => void,
  delayMs: number = DRAFT_SAVE_DEBOUNCE_MS,
): DebouncedSaver {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending = false;
  const run = () => {
    timer = null;
    pending = false;
    save();
  };
  return {
    schedule() {
      pending = true;
      if (timer != null) clearTimeout(timer);
      timer = setTimeout(run, delayMs);
    },
    flush() {
      if (timer != null) {
        clearTimeout(timer);
        timer = null;
      }
      if (pending) {
        pending = false;
        save();
      }
    },
    cancel() {
      if (timer != null) clearTimeout(timer);
      timer = null;
      pending = false;
    },
  };
}
